import React from 'react';
import { useBachQuery } from '/@services/bach/bach';
import {
    AnswerKindEnum,
    recallAnswerKind,
} from '/@services/bach/spotql-answer-kind-store';
import {
    Eureka_AnswerKind,
    GetAnswerExplanationDocument,
    GetAnswerExplanationQuery,
    GetAnswerExplanationQueryVariables,
} from '/@services/generated/graphql-types';

const STATUS_OK = 'OK';

type ExplanationSession = { sessionId?: string; genNo?: number | null };

// Per session: each switch genNo, mapped to the genNo it only re-draws.
const queryGenNoBySwitchGenNo = new Map<string, Map<number, number>>();

// Mounted explanation queries per session; only these can hold a session.
const mountedExplanationQueries = new Map<string, number>();

// Backstops on a tab-lifetime map, not product tuning.
export const SWITCH_MAP_LIMITS = {
    sessions: 50,
    // Only the current genNo is resolved, so older switches are dead weight.
    switchesPerSession: 20,
} as const;

// Both maps keep insertion order, so the first key is the oldest.
const trimOldest = (entries: Map<unknown, unknown>, max: number): void => {
    while (entries.size > max) {
        const oldest = entries.keys().next().value;
        if (oldest === undefined) {
            return;
        }
        entries.delete(oldest);
    }
};

const getQueryGenNo = (sessionId: string, genNo: number): number =>
    queryGenNoBySwitchGenNo.get(sessionId)?.get(genNo) ?? genNo;

export const hasActiveExplanationQuery = (sessionId: string): boolean =>
    mountedExplanationQueries.has(sessionId);

const retainExplanationSession = (sessionId: string): void => {
    mountedExplanationQueries.set(
        sessionId,
        (mountedExplanationQueries.get(sessionId) ?? 0) + 1,
    );
};

// Drops the session's switches with its last query: nothing can resolve them.
const releaseExplanationSession = (sessionId: string): void => {
    const mounted = (mountedExplanationQueries.get(sessionId) ?? 0) - 1;
    if (mounted > 0) {
        mountedExplanationQueries.set(sessionId, mounted);
        return;
    }
    mountedExplanationQueries.delete(sessionId);
    queryGenNoBySwitchGenNo.delete(sessionId);
};

export const recordDisplayModeSwitch = (
    sessionId: string,
    fromGenNo?: number | null,
    toGenNo?: number | null,
): void => {
    if (
        !Number.isFinite(fromGenNo) ||
        !Number.isFinite(toGenNo) ||
        fromGenNo === toGenNo
    ) {
        return;
    }
    const genNos =
        queryGenNoBySwitchGenNo.get(sessionId) ?? new Map<number, number>();
    genNos.set(Number(toGenNo), getQueryGenNo(sessionId, Number(fromGenNo)));
    trimOldest(genNos, SWITCH_MAP_LIMITS.switchesPerSession);
    queryGenNoBySwitchGenNo.set(sessionId, genNos);
    trimOldest(queryGenNoBySwitchGenNo, SWITCH_MAP_LIMITS.sessions);
};

// eslint-disable-next-line no-underscore-dangle
export const __resetDisplayModeSwitchesForTests__ = (): void => {
    queryGenNoBySwitchGenNo.clear();
    mountedExplanationQueries.clear();
};

const isSameQuery = (
    held: ExplanationSession,
    current: ExplanationSession,
): boolean =>
    !!held.sessionId &&
    held.sessionId === current.sessionId &&
    // Number(null) is 0, so without these two null genNos would match.
    Number.isFinite(held.genNo) &&
    Number.isFinite(current.genNo) &&
    getQueryGenNo(held.sessionId, Number(held.genNo)) ===
        getQueryGenNo(held.sessionId, Number(current.genNo));

// Fetches the answer explanation via prism getEurekaAnswerExplanation.
export const useGetAnswerExplanationQuery = (answerSessionId: string) => {
    // Any other/absent kind keeps the server's TML_ANSWER default.
    const isSpotql =
        recallAnswerKind(answerSessionId) === AnswerKindEnum.Spotql;
    // Hold the last session across a switch so apollo sees no change.
    const heldSessionRef = React.useRef<ExplanationSession | undefined>();
    const transformSession = React.useCallback(
        (session?: ExplanationSession | null) => {
            if (!session) {
                heldSessionRef.current = undefined;
                return session;
            }
            const held = heldSessionRef.current;
            if (held && isSameQuery(held, session)) {
                return held;
            }
            heldSessionRef.current = session;
            return session;
        },
        [],
    );
    // Gates recording at the call site; drops switches on the last unmount.
    React.useEffect(() => {
        retainExplanationSession(answerSessionId);
        return () => releaseExplanationSession(answerSessionId);
    }, [answerSessionId]);
    const { data, error, loading } = useBachQuery<
        GetAnswerExplanationQuery,
        GetAnswerExplanationQueryVariables
    >(answerSessionId, GetAnswerExplanationDocument, {
        variables: {
            answerKind: isSpotql ? Eureka_AnswerKind.SpotqlAnswer : undefined,
        },
        handledErrors: ['*'],
        // Consumed by useStatefulQuery, outside the apollo option types.
        ...{ transformSession },
    });

    const result = data?.getEurekaAnswerExplanation;
    // A non-OK status carries no usable explanation.
    const isError = !!error || (!!result && result.status !== STATUS_OK);

    return {
        explanation: (!isError && result?.explanation) || '',
        isError,
        loading,
    };
};
